// ============================================================================
// CoreModule - Singleton Services & HTTP Interceptors
// ============================================================================
// Imported once in AppModule via forRoot(). Provides all singleton services
// (Auth, Data, Session, Tenant, Permissions, etc.) and registers interceptors
// for loading state tracking and cookie-based auth with 401 redirect.
// ============================================================================

import { NgModule, Optional, SkipSelf, ModuleWithProviders } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { LoadingService } from './loading.service';
import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { SessionService } from './session.service';
import { TenantService } from './tenant.service';
import { PermissionsService } from './permissions.service';
import { ListFilterService } from './list-filter.service';
import { QuoteService } from './quote.service';
import { NotificationService } from './notification.service';
import { BreadcrumbService } from './breadcrumb.service';

// Tracks loading state for all HTTP requests
@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  constructor(private loading: LoadingService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.loading.onStarted(req);
    return next.handle(req).pipe(
      finalize(() => this.loading.onFinished(req))
    );
  }
}

// Ensures cookies (JWT auth token) are sent with every API request
// Also handles session timeout (401) by redirecting to login
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // URL prefixes that legitimately run without auth — a 401 from these means
  // "no session" (expected on /login, /login/reset, /login/extquote/...),
  // NOT "session expired", so we must NOT auto-redirect to /login or we'd
  // bounce the user off the very pages they need to use signed-out.
  private static readonly ANON_PREFIXES = ['/api/auth/', '/api/external/'];

  constructor(private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authReq = req.clone({ withCredentials: true });
    return next.handle(authReq).pipe(
      tap({
        error: (err: any) => {
          if (err.status !== 401) return;
          if (AuthInterceptor.ANON_PREFIXES.some(p => req.url.includes(p))) return;
          // Session expired on an authenticated endpoint — bounce to login.
          this.router.navigate(['/login']);
        }
      })
    );
  }
}

// Surfaces server-side save failures as a visible toast so silent failures
// don't go unnoticed when callsites bare-subscribe to dsSave().
//
// Background: /api/datagetter/ds used to swallow exceptions and return HTTP
// 200 + body "0", making errors indistinguishable from zero-row updates.
// The server now returns HTTP 500 on exception; this interceptor catches
// that (and any other 5xx on datagetter routes) and toasts the message.
// Callsites with their own .error handlers still receive the RxJS error —
// this layer is purely additive feedback.
@Injectable()
export class DataErrorInterceptor implements HttpInterceptor {
  constructor(private messageSvc: MessageService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          if (err.status < 500) return;
          if (!req.url.includes('/api/datagetter/')) return;
          // The DS endpoint returns the exception message as plain text in
          // err.error. dg / getdata might still throw in unusual cases — use
          // a generic fallback message for them.
          const isSave = req.url.endsWith('/ds');
          const detail = (typeof err.error === 'string' && err.error.trim())
            ? err.error.trim()
            : (isSave ? 'The change could not be saved.' : 'A server error occurred.');
          this.messageSvc.add({
            severity: 'error',
            summary: isSave ? 'Save failed' : 'Server error',
            detail,
            life: 6000
          });
        }
      })
    );
  }
}

@NgModule({
  imports: [CommonModule],
  exports: [CommonModule],
  providers: [
    LoadingService,
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: DataErrorInterceptor, multi: true },
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })
    )
  ]
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in AppModule only.');
    }
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        AuthService,
        DataService,
        SessionService,
        TenantService,
        PermissionsService,
        ListFilterService,
        QuoteService,
        NotificationService,
        BreadcrumbService
      ]
    };
  }
}
